import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, EditableCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, shortName, toParagraph } from '../utils/quinn';
import { useIsEditing, useRawProperty, useReducedMotion } from '../utils/hooks';
import { Lightbox, useLightbox } from '../utils/lightbox';
import { CAT_LABEL, photoTiles } from '../utils/gallery';

interface Photo {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
  description?: string;
}

const Arrow = ({ dir }: { dir: 'prev' | 'next' }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path d={dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
  </svg>
);

const arrowBtn =
  'inline-flex h-12 w-12 items-center justify-center rounded-full border border-solid [transition:transform_var(--q-motion-normal)_var(--q-ease-out),opacity_var(--q-motion-fast)] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-linejoin:round] [&_svg]:[stroke-width:1.8] [&:hover:not(:disabled)]:[transform:scale(1.06)] disabled:cursor-default disabled:opacity-40';

/** "See it for yourself" (Paper 11): a swipeable row of gallery photos with arrows and a progress rule. */
export function TourGallery() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const reduced = useReducedMotion();
  const lb = useLightbox();
  const photos = useCollectionBinding<Photo>('tourgallery.photos', { collection: 'photo_gallery', defaultLimit: 12 });
  const trackRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({ progress: 0, prev: false, next: false });

  const tiles = photoTiles(photos);
  const name = shortName(clean(p.property_name));

  const measure = useCallback(() => {
    const t = trackRef.current;
    if (!t) return;
    const max = t.scrollWidth - t.clientWidth;
    setState({ progress: max > 0 ? (t.scrollLeft + t.clientWidth) / t.scrollWidth : 1, prev: t.scrollLeft > 4, next: t.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    const t = trackRef.current;
    if (!t) return;
    measure();
    t.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(t);
    return () => {
      t.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [measure, tiles.length]);

  if (!tiles.length && !editing) return null;

  const step = (d: number) => {
    const t = trackRef.current;
    const card = t?.querySelector<HTMLElement>('.tgal__tile');
    t?.scrollBy({ left: (card ? card.offsetWidth + 24 : 480) * d, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <section className="flex flex-col gap-10 overflow-hidden pb-4xl max-[767px]:gap-6 max-[767px]:pb-12 max-[767px]:pt-14">
      <div className="flex items-end justify-between gap-6 px-[--gutter]" data-reveal>
        <div className="flex max-w-[640px] flex-col gap-3.5 [&_.h2_p]:m-0">
          <RichTextContent path="tourgallery.eyebrow" as="div" className="eyebrow" defaultValue={toParagraph(['Gallery', name].filter(Boolean).join(' · '))} />
          <RichTextContent path="tourgallery.title" as="h2" className="h2" />
        </div>
        <div className="flex gap-3 max-[767px]:hidden">
          <button className={`${arrowBtn} border-[--q-border] bg-[--q-paper] text-[--q-ink]`} type="button" aria-label="Previous photos" disabled={!state.prev} onClick={() => step(-1)}>
            <Arrow dir="prev" />
          </button>
          <button className={`${arrowBtn} border-[--q-primary] bg-[--q-primary] text-[--q-on-primary]`} type="button" aria-label="Next photos" disabled={!state.next} onClick={() => step(1)}>
            <Arrow dir="next" />
          </button>
        </div>
      </div>
      <EditableCollectionBinding bindingPath="tourgallery.photos" label="Photos">
        <div
          className="flex gap-6 overflow-x-auto px-[--gutter] [scroll-padding:0_var(--gutter)] [scroll-snap-type:x_mandatory] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-[767px]:gap-3"
          ref={trackRef}
        >
          {tiles.map((t, i) => (
            <button
              key={`${t.src}-${i}`}
              className="tgal__tile"
              type="button"
              onClick={() => lb.open(i)}
              aria-label={`Open photo: ${t.caption || t.alt || 'photo'}`}
            >
              <VibesitesImage url={t.src} alt={t.alt} sizes="(min-width: 768px) 460px, 280px" />
            </button>
          ))}
        </div>
      </EditableCollectionBinding>
      <div className="flex items-center justify-between gap-4 px-[--gutter]">
        <div className="relative h-0.5 flex-auto bg-[--q-border] max-[767px]:flex-[0_0_200px]" aria-hidden="true">
          <span
            className="absolute left-0 top-0 h-0.5 bg-[--q-primary] [transition:width_var(--q-motion-normal)_var(--q-ease-out)]"
            style={{ width: `${Math.round(state.progress * 100)}%` }}
          />
        </div>
        <RichTextContent path="tourgallery.swipe_hint" as="span" className="hidden text-[13px] leading-[18px] text-[--q-muted] [&_p]:m-0 max-[767px]:block" />
      </div>
      <Lightbox
        items={tiles.map((t) => ({ src: t.src, alt: t.alt, caption: t.caption, chip: CAT_LABEL[t.cat], desc: t.desc }))}
        index={lb.index}
        onClose={lb.close}
        onIndex={lb.setIndex}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourGallery',
  displayName: 'Tour Gallery',
  tokens: {
    'tourgallery.eyebrow': { label: 'Eyebrow', description: 'Until edited, shows "Gallery · " and the community name.', type: 'richtext', defaultValue: '<p>Gallery</p>' },
    'tourgallery.title': { label: 'Title', type: 'richtext', defaultValue: '<p>See it for yourself.</p>' },
    'tourgallery.swipe_hint': { label: 'Swipe Hint (phone)', type: 'richtext', defaultValue: '<p>Swipe for more</p>' },
  },
  collectionBindings: {
    'tourgallery.photos': {
      label: 'Photos',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  },
};

(TourGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourGallery;
